/**
 * AI-referrer channel classifier (GEO backlog P4).
 *
 * AI-search engines (ChatGPT, Perplexity, Gemini/Google AI, Claude, Copilot,
 * Bing Chat, …) send real organic traffic that PostHog's default channel model
 * buries inside generic "Referral" / "Organic Search". PR #1450 found ChatGPT is
 * already the #1 non-Google referrer (~4.5% of pageviews) and growing, so the
 * founder wants AI-search trackable as its own first-class channel.
 *
 * This module is a small **pure** function: it maps a referrer hostname to a
 * normalised AI-source label. The provider (`posthog-provider.tsx`) calls it on
 * each `$pageview` with the cross-origin `document.referrer` host and, when it
 * resolves, sets `ai_referrer` (event + person property). Build a PostHog
 * insight/dashboard breakdown on that property — see
 * `docs/analysis/ai-referrer-channel.md`.
 *
 * No PII, no network, no bundle weight beyond a static table — safe to import
 * anywhere on the request path.
 */

/**
 * Canonical AI-referrer source labels. `other` means "matched an AI engine we
 * group together but don't break out individually" (kept narrow on purpose so
 * the channel stays meaningful); a non-AI referrer returns `null` so the caller
 * can simply skip setting the property.
 */
export type AiReferrerSource =
	| "chatgpt"
	| "perplexity"
	| "gemini"
	| "claude"
	| "copilot"
	| "other";

/**
 * Suffix-matched host → label table. Each entry matches the exact host or any
 * subdomain of it (e.g. `chat.openai.com` and `chatgpt.com` both → `chatgpt`).
 * Order does not matter; the longest semantic grouping wins by virtue of being
 * an explicit entry. Keep this list tight: only hosts that are unambiguously an
 * AI-search / AI-assistant surface belong here.
 */
const AI_REFERRER_HOSTS: ReadonlyArray<readonly [string, AiReferrerSource]> = [
	// OpenAI ChatGPT (web app + the search-with-citations surface).
	["chatgpt.com", "chatgpt"],
	["chat.openai.com", "chatgpt"],
	["openai.com", "chatgpt"],
	["oai.azure.com", "chatgpt"],

	// Perplexity.
	["perplexity.ai", "perplexity"],

	// Google Gemini / Google AI surfaces. (Plain google.com AI Overviews are NOT
	// here — they arrive under the normal Google organic referrer with no
	// distinguishing host, so we cannot separate them client-side without false
	// positives. gemini.google.com / bard.google.com are the dedicated apps.)
	["gemini.google.com", "gemini"],
	["bard.google.com", "gemini"],

	// Anthropic Claude.
	["claude.ai", "claude"],
	["claude.com", "claude"],

	// Microsoft Copilot + the legacy Bing Chat host. (`bing.com` itself stays
	// out — that is ordinary Bing organic search, not the AI assistant.)
	["copilot.microsoft.com", "copilot"],
	["copilot.cloud.microsoft", "copilot"],
	["m365.cloud.microsoft", "copilot"],

	// Other broadly-recognised AI-assistant / AI-answer surfaces grouped under
	// "other" so the channel captures the long tail without fragmenting.
	["you.com", "other"],
	["grok.com", "other"],
	["meta.ai", "other"],
	["poe.com", "other"],
	["phind.com", "other"],
	["andisearch.com", "other"],
	["mistral.ai", "other"],
	["chat.mistral.ai", "other"],
	// `x.com` and `duckduckgo.com` were both here ("Grok answers link out with
	// x.com host" / "DuckAssist AI answers") but both hosts are also a
	// first-class channel in `src/lib/analytics/channel.ts`'s precedence table
	// (rule 12 `x`, rule 11 `organic_search`) — rule 10 (this classifier) runs
	// BEFORE both, so leaving them here silently reclassified every ordinary
	// X referral and every DuckDuckGo organic search as `ai_search`. Removed
	// 2026-09 (plan: canonical marketing-channel classifier, P0.2) — a
	// deliberate breaking change to the existing `ai_referrer` property.
];

/**
 * Normalise a hostname: lowercase and strip a leading `www.` so `www.claude.ai`
 * and `claude.ai` classify the same.
 */
function normalizeHost(host: string): string {
	const lower = host.trim().toLowerCase();
	return lower.startsWith("www.") ? lower.slice(4) : lower;
}

/**
 * Classify a referrer hostname into a named AI-search source, or `null` when it
 * is not an AI referrer.
 *
 * Matching is exact-host **or** subdomain-of (suffix on a dot boundary), so
 * `chatgpt.com`, `www.chatgpt.com`, and `eu.chatgpt.com` all map to `chatgpt`
 * while `notchatgpt.com` does not.
 *
 * @param hostname the referrer host (e.g. `document.referrer`'s `URL.hostname`)
 */
export function classifyAiReferrer(
	hostname: string | null | undefined,
): AiReferrerSource | null {
	if (!hostname) return null;
	const host = normalizeHost(hostname);
	if (host.length === 0) return null;

	for (const [domain, source] of AI_REFERRER_HOSTS) {
		if (host === domain || host.endsWith(`.${domain}`)) {
			return source;
		}
	}
	return null;
}
